import _ from 'lodash'
import React, {
  useContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
  ComponentType,
} from 'react'
import * as amplitudeClient from '@amplitude/unified'
import { FeatureFlags, hooks } from '@adc/ui-core'
import coreLogger from '@adc/logger'
import type { primitives as BlueprintPrimitivesTypes } from '@adc/blueprint'
// TODO; remove this once we're sure all usages of old notifier are done away with
import { NullNotifier } from './notifiers'
import type { Notifier } from './notifiers'
import { primitives } from '@adc/blueprint'
import canUseDom from '../../util/canUseDom'
import { NUDGE_CONSTANTS } from '../../util/investorPreferences'
import type { Capabilities } from './types'

type EmbeddedContext = 'browser' | 'webview' | ''

/**
 * Application-layer notifications structure.
 * This is a transformed version of the SDK's StandardOffsetPagination type,
 * with the following changes:
 * - Pagination fields nested under `pagination` key (instead of flat)
 * - Snake_case converted to camelCase (total_pages -> totalPages)
 * - Content array renamed to `data` (content -> data)
 *
 * The transformation happens in:
 * @see applicationSessionInformation/applicationSessionInformation.jsx (lines 36-46)
 *
 * To remove this custom interface, refactor the transformation logic to use
 * StandardOffsetPagination directly from the SDK.
 *
 * @see StandardOffsetPagination from @adc/sdk/types for the original API structure
 */
export interface Notifications {
  data: unknown[]
  pagination: {
    page: number
    take: number
    totalPages: number | null
    totalElements: number | null
  }
}

interface ApplicationContextValue {
  isCurrentlyFetching: boolean
  setIsFetching: (isFetching: boolean) => void
  isMaintenanceEnabled: boolean
  sspConfig: Record<string, unknown>
  user: BlueprintPrimitivesTypes.AuctionUser
  auth: Record<string, unknown>
  featureFlags: FeatureFlags
  logger: typeof coreLogger
  modalState: string
  setModalState: (state: string) => void
  loginOrSignup: string
  setLoginOrSignup: (value: string) => void
  notifications: Notifications
  setNotifications: (notifications: Notifications) => void
  graphWebsocket: primitives.IGraphWebsocket
  notifier: Notifier
  capabilities: Capabilities
  actions: {
    updateUser: (
      updatedData: Partial<BlueprintPrimitivesTypes.AuctionUser>
    ) => void
    onOnboardingComplete: () => void
  }
  embeddedContext: EmbeddedContext
  applicationSessionInformation: Record<string, unknown>
}

interface ApplicationContextProviderProps {
  children: ReactNode
  sspConfig?: Record<string, unknown>
  user?: BlueprintPrimitivesTypes.AuctionUser
  auth?: Record<string, unknown>
  featureFlags?: Record<string, unknown>
  logger?: typeof coreLogger
  notifier?: Notifier
  graphWebsocket?: primitives.IGraphWebsocket
  capabilities?: Capabilities
  embeddedContext?: EmbeddedContext
}

export const ApplicationContextShape: ApplicationContextValue = {
  // Global indicator for whether async work is currently happening
  isCurrentlyFetching: false,
  setIsFetching: () => {},

  // Global indicator for whether our apps should show they are in maintenance mode. This has been used in the past to
  // communicate during scheduled maintenance windows, or when performance degrades enough to warrant informing the user
  // somehow without bringing the whole site offline.
  //
  // TODO: Hook this up with the maintenance message we show today based on feature flags
  isMaintenanceEnabled: false,

  // Logger specific for this application
  logger: coreLogger.forModule('AUCTIONCOM'),

  // this will store either browser or webview to indicate whether the app is running within a  web browser or part of the mobile app
  embeddedContext: '',

  // Single instance of a notification system that all components in this application should use by default.
  notifier: new NullNotifier(),
  graphWebsocket: new primitives.NullGraphWebsocket(),
  // TODO: Can we make this a bitset, which makes it trivial to ensure only a single bit is every flipped, which
  // translates into only ever showing a single modal. If we ever create a stacking context with multiple modals, then
  // that is really a bug.
  modalState: '',
  setModalState: () => {},
  loginOrSignup: 'login',
  setLoginOrSignup: () => {},
  featureFlags: {},
  sspConfig: {},
  user: { logged_in: false },
  auth: {},
  notifications: {
    data: [],
    pagination: {
      page: 0,
      take: 20,
      totalPages: null,
      totalElements: null,
    },
  },
  setNotifications: () => {},
  capabilities: {
    authentication: {
      enabled: true,
      options: {
        onAuthenticationComplete: () => {},
        onOnboardingComplete: () => {},
      },
    },
  },
  actions: {
    updateUser: () => {},
    onOnboardingComplete: () => {},
  },
  applicationSessionInformation: {},
}

// TODO: Should I export this? Can't see a need for it directly now. And I export the consumer and provider down below
// to do the same thing.
export const ApplicationContext = React.createContext<ApplicationContextValue>(
  ApplicationContextShape as unknown as ApplicationContextValue
)
export const ApplicationContextConsumer = ApplicationContext.Consumer

/**
 * Merge a partial update into user data. Arrays are replaced wholesale (not
 * merged by index, which is lodash merge's default) so a local override can
 * shrink a list — e.g. removing a buying-preferences location. Objects still
 * deep-merge, preserving untouched keys.
 */
export const mergeUserData = <
  TBase extends Partial<BlueprintPrimitivesTypes.AuctionUser>
>(
  base: TBase,
  update: Partial<BlueprintPrimitivesTypes.AuctionUser>
): TBase =>
  _.mergeWith({}, base, update, (_objValue, srcValue) =>
    Array.isArray(srcValue) ? srcValue : undefined
  )

export const ApplicationContextProvider = (
  props: ApplicationContextProviderProps
) => {
  const { children } = props
  // Access the current context value to support nested providers. When an ApplicationContextProvider is nested inside
  // another one, this allows the child provider to inherit values from the parent provider that weren't explicitly
  // passed as props. This enables composition patterns where parent providers can set baseline values and child
  // providers can override only specific properties.
  const currentContext = useContext(ApplicationContext)

  // The logic below is structured as it is because we don't want valid properties in the props object to be overwritten
  // by empty ones in the ApplicationContextShape (which is why we can't simply spread the objects together). isNil is
  // needed because there are valid false cases - see `isMaintenanceEnabled` as one example -- so it's needed to
  // differentiate between undefined/null and false to correctly merge the objects without losing the values we want.
  //
  // The reduce operation builds an object containing all provider values with proper fallback priority.
  // For each key in ApplicationContextShape, we check three sources in order:
  // 1. propsValue - Explicit props passed to this provider (highest priority)
  // 2. currentContextValue - Values from a parent provider if one exists (middle priority)
  // 3. defaultContextValue - Default values from ApplicationContextShape (lowest priority)
  //
  // The accumulator (acc) builds up the final configuration object key by key.
  const {
    sspConfig,
    user,
    auth,
    featureFlags,
    logger,
    notifier,
    graphWebsocket,
    capabilities,
    embeddedContext,
  } = (
    Object.keys(ApplicationContextShape) as Array<keyof ApplicationContextValue>
  ).reduce((acc, key) => {
    const propsValue = props[key as keyof ApplicationContextProviderProps]
    const currentContextValue = currentContext[key]
    const defaultContextValue = ApplicationContextShape[key]

    // NOTE: If we ever explicitly want null passed in here we would need to change this condition to check for
    // `undefined `
    acc[key] =
      [propsValue, currentContextValue].find(v => !_.isNil(v)) ||
      defaultContextValue

    return acc
  }, {} as Record<string, unknown>) as Pick<
    ApplicationContextValue,
    | 'sspConfig'
    | 'user'
    | 'auth'
    | 'featureFlags'
    | 'logger'
    | 'notifier'
    | 'graphWebsocket'
    | 'capabilities'
    | 'embeddedContext'
  >

  const [isCurrentlyFetching, setIsFetching] = useState(false)

  const [modalState, setModalState] = useState('')
  const [loginOrSignup, setLoginOrSignup] = useState('login')

  const [notifications, setNotifications] = useState<Notifications>(
    ApplicationContextShape.notifications
  )

  const [localUserData, setLocalUserData] = useState<
    Partial<BlueprintPrimitivesTypes.AuctionUser>
  >({})

  const updateUser = (
    updatedData: Partial<BlueprintPrimitivesTypes.AuctionUser>
  ) => {
    setLocalUserData(currentData => mergeUserData(currentData, updatedData))
  }

  const onOnboardingComplete = () => {
    const updatedOnboardingAnswers =
      NUDGE_CONSTANTS().REQUIRED_QUESTION_CODES.map(value => ({
        question_code: value,
      }))
    updateUser({ onboarding_answers: updatedOnboardingAnswers })
  }

  const isLoggedIn = 'logged_in' in user && user.logged_in
  const wasLoggedIn = hooks.usePrevious(isLoggedIn)

  useEffect(() => {
    if (isLoggedIn && !wasLoggedIn) {
      graphWebsocket.restart()
    }
  }, [isLoggedIn, graphWebsocket, wasLoggedIn])

  useEffect(() => {
    if (isLoggedIn && !wasLoggedIn) {
      // sets amplitude user id
      amplitudeClient.setUserId(user.user_id)
      amplitudeClient.identify(
        new amplitudeClient.Identify().set('loggedIn', true)
      )
    }

    // @ts-ignore user_id
  }, [isLoggedIn, wasLoggedIn, user?.user_id])

  hooks.useMountEffect(() => () => graphWebsocket.close())

  // Memoize user object to prevent unnecessary re-renders in consuming components from the object reference changing
  const mergedUser = useMemo(
    () => mergeUserData(user, localUserData),
    [user, localUserData]
  )

  const providerValues: ApplicationContextValue = {
    isCurrentlyFetching,
    setIsFetching,
    isMaintenanceEnabled: ApplicationContextShape.isMaintenanceEnabled,
    sspConfig,
    user: mergedUser,
    auth,
    featureFlags,
    logger,
    modalState,
    setModalState,
    loginOrSignup,
    setLoginOrSignup,
    notifications, // TODO: deprecate, use useApplicationNotifications instead
    setNotifications, // TODO: deprecate, use useApplicationNotifications instead
    graphWebsocket,
    notifier,
    capabilities,
    actions: {
      updateUser,
      onOnboardingComplete,
    },
    embeddedContext,
    applicationSessionInformation:
      ApplicationContextShape.applicationSessionInformation,
  }

  if (embeddedContext) {
    // when passed in (e.g. from stories or test)
    providerValues.embeddedContext = embeddedContext
  } else if (
    // otherwise detect from the URL - this is for real-time mobile webview detection
    canUseDom() &&
    new URLSearchParams(window.location.search).get('deviceContext') ===
      'webview'
  ) {
    providerValues.embeddedContext = 'webview'
  } else {
    // default
    providerValues.embeddedContext = 'browser'
  }

  return (
    <ApplicationContext.Provider value={providerValues}>
      {children}
    </ApplicationContext.Provider>
  )
}

// For use with function components using hooks
export const useApplicationContext = () => useContext(ApplicationContext)

// A lot of our components are not in a state where we can drop in hooks, so defining this
// HoC here for use with existing code.
export const withApplicationContext =
  <P extends object>(
    Component: ComponentType<
      P & { applicationContext: ApplicationContextValue }
    >
  ) =>
  (props: P) =>
    (
      <ApplicationContextConsumer>
        {context => <Component applicationContext={context} {...props} />}
      </ApplicationContextConsumer>
    )
